import { useEffect, useMemo, useState } from 'react'
import type {
  DataTablePaginationProps,
  DataTableSortDirection,
} from '@enamelai/design-system'

type SortValue = string | number | null | undefined

type FormsTableState<T> = {
  pageRows: T[]
  sortColumnId?: string
  sortDirection?: DataTableSortDirection
  onSort: (columnId: string) => void
  pagination: DataTablePaginationProps
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export function useFormsTableState<T>(
  rows: T[],
  getSortValue: (row: T, columnId: string) => SortValue,
  initialPageSize = 10,
): FormsTableState<T> {
  const [sortColumnId, setSortColumnId] = useState<string | undefined>()
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)

  const sortedRows = useMemo(() => {
    if (!sortColumnId) return rows
    return [...rows].sort((left, right) => {
      const leftValue = getSortValue(left, sortColumnId)
      const rightValue = getSortValue(right, sortColumnId)
      if (leftValue == null) return 1
      if (rightValue == null) return -1
      const compared =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, {
              sensitivity: 'base',
            })
      return sortDirection === 'asc' ? compared : -compared
    })
  }, [getSortValue, rows, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize))

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const pageRows = useMemo(
    () =>
      sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [currentPage, pageSize, sortedRows],
  )

  const onSort = (columnId: string): void => {
    setSortDirection((direction) =>
      sortColumnId === columnId && direction === 'asc' ? 'desc' : 'asc',
    )
    setSortColumnId(columnId)
    setCurrentPage(1)
  }

  return {
    pageRows,
    sortColumnId,
    sortDirection: sortColumnId ? sortDirection : undefined,
    onSort,
    pagination: {
      currentPage,
      totalPages,
      pageSize,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      onPageChange: setCurrentPage,
      onPageSizeChange: (size: number): void => {
        setPageSize(size)
        setCurrentPage(1)
      },
    },
  }
}
